import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * LegalLayout — clean, printable, mobile-first shell for /privacy, /terms, /photo-release.
 * No bottom nav (these aren't Event Hub pages) — just a back link + brand mark.
 */
export default function LegalLayout({ title, effectiveDate, children }) {
  return (
    <div className="min-h-screen bg-white text-emerald-950">
      {/* Print-safe header — includes brand + back link on screen, but auto-hides in print */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-emerald-900/10 print:hidden">
        <div className="mx-auto max-w-3xl flex items-center justify-between px-4 py-3">
          <Link to="/" data-testid="legal-back" className="inline-flex items-center gap-1.5 text-emerald-950/70 hover:text-fuchsia-700 text-sm font-[Outfit] font-semibold">
            <ArrowLeft size={16} /> Back
          </Link>
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-emerald-950 text-white flex items-center justify-center font-[Cormorant_Garamond] text-sm font-bold">
              F<span className="text-fuchsia-400">i</span>M
            </span>
            <span className="font-[Cormorant_Garamond] text-base text-emerald-950">Business Showers</span>
          </div>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-6 py-10 print:max-w-none print:px-0">
        <div className="mb-6">
          <div className="text-[10px] tracking-[0.24em] uppercase font-[Outfit] font-bold text-fuchsia-700">Legal</div>
          <h1 className="mt-1 font-[Cormorant_Garamond] text-4xl sm:text-5xl leading-[1.05] text-emerald-950">{title}</h1>
          {effectiveDate && (
            <p className="mt-2 font-[Outfit] text-xs text-emerald-950/60">
              Effective date: <span className="font-semibold text-emerald-950">{effectiveDate}</span>
            </p>
          )}
        </div>

        <div className="prose prose-emerald max-w-none font-[Outfit] text-sm sm:text-base text-emerald-950/85 leading-relaxed
          [&_h2]:font-[Cormorant_Garamond] [&_h2]:text-2xl [&_h2]:sm:text-3xl [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-emerald-950
          [&_h3]:font-[Outfit] [&_h3]:font-bold [&_h3]:text-emerald-950 [&_h3]:text-base [&_h3]:mt-5 [&_h3]:mb-1
          [&_p]:mb-3
          [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3 [&_ul]:space-y-1
          [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3 [&_ol]:space-y-1
          [&_a]:text-fuchsia-700 [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-fuchsia-900
          [&_strong]:text-emerald-950 [&_code]:bg-emerald-50 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-emerald-900 [&_code]:text-[13px]">
          {children}
        </div>

        <footer className="mt-16 pt-6 border-t border-emerald-900/10 text-xs font-[Outfit] text-emerald-950/60 space-y-1 print:mt-8">
          <p>
            Freedom In Me · Business Showers · Charlotte, NC · <a href="mailto:info@itsthefreedominme.com">info@itsthefreedominme.com</a>
          </p>
          <p className="flex flex-wrap gap-3">
            <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="hover:underline">Terms of Use</Link>
            <Link to="/photo-release" className="hover:underline">Photo & Video Release</Link>
          </p>
        </footer>
      </article>
    </div>
  );
}
